import { useCallback, useEffect, useRef, useState } from 'react';

import { CloudAlertIcon, CloudUploadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import { uploadSigned } from 'helpers/aws-s3';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';

import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { StatusPill } from 'components/Pills/StatusPills';

export const DOCUMENT_UPLOAD_SLOT_STATE = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  UPLOAD_FAILED: 'upload_failed',
  UPLOADED_PENDING: 'uploaded_pending',
  VERIFY_FAILED_RETRY: 'verify_failed_retry',
  VERIFY_FAILED_FACE_MISMATCH: 'verify_failed_face_mismatch',
  FORCE_ACCEPTED: 'force_accepted',
  VERIFIED: 'verified',
};

const formatMegabytes = bytes => {
  if (!bytes) return null;
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 1024 * 1024 ? 1 : 2)}MB`;
};

export const DocumentUploadSlot = ({
  side,
  heading,
  caption = '',
  description = '',
  additionalInstructions = '',
  brand,
  disabled = false,
  hideDeleteButton = false,
  rejected = false,
  showTitle = true,
  // When true, snap back to dropzone after upload (MultipleDocumentUpload "add file" mode).
  resetAfterUpload = false,
  // Own upload lifecycle (FilePicker-like) for standalone single-file use.
  value,
  useModel = false,
  modelParams = {},
  metadata = {},
  bucketFolder = FilesFolders.CandidatesApplicationDocuments,
  maxSize = 20971520,
  accept,
  acceptLabel = '',
  isFileAllowed = () => true,
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  onUploadingChange = () => {},

  // Optional verification overlay from DocumentDualUpload; absent for standalone upload.
  verification,

  className,
}) => {
  const { t } = useTranslation();
  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const initializedRef = useRef(undefined);

  const [fileName, setFileName] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // ─── Hydrate from `value` prop ─────────────────────────────────────────────
  useEffect(() => {
    const valueId = useModel ? (value?.id ?? null) : (value ?? null);
    // eslint-disable-next-line eqeqeq
    if (initializedRef.current == valueId && initializedRef.current !== undefined) return;
    initializedRef.current = valueId;

    if (!value) {
      setFileName(null);
      setFileSize(null);
      return;
    }
    // file_size lives in metadata (no backend column); safe to re-set after live upload too.
    if (useModel) {
      setFileName(value.file_name);
      setFileSize(value.metadata?.file_size ?? null);
    } else {
      getUpload(value)
        .then(upload => {
          if (upload) {
            setFileName(upload.file_name);
            setFileSize(upload.metadata?.file_size ?? null);
          }
        })
        .catch(() => {
          // Transient error — prop value still exists, keep existing state
        });
    }
  }, [value, useModel, getUpload]);

  // Shared validation/upload path for click-select and drag-and-drop.
  const processFile = async file => {
    if (!file) return;

    if (file.size > maxSize) {
      setUploadError(
        `File is too large. Max size is ${Math.round(maxSize / 1024 / 1024)}MB. Please upload a smaller file.`,
      );
      return;
    }
    if (!isFileAllowed(file)) {
      setUploadError(`This file type is not supported. Please use ${acceptLabel}.`);
      return;
    }

    setLoading(true);
    onUploadingChange(true);
    setUploadError(null);
    // Clear old filename immediately so it never flashes next to "Uploading...".
    setFileName(null);
    setFileSize(null);

    const prevUploadId = useModel ? value?.id : value;

    try {
      const signedFile = await signUpload({
        file_name: file.name,
        file_type: file.type,
        file_ext:
          mime.extension(file.type) ||
          (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
          'bin',
        bucket_folder: bucketFolder,
      });

      await uploadSigned(signedFile.url, file, file.type);

      const upload = await createUpload({
        ...modelParams,
        // Stamp file_size here — only the slot sees the File; enables reload hydration.
        metadata: { ...metadata, file_size: file.size },
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      });

      if (prevUploadId && prevUploadId !== upload.id) {
        try {
          await deleteUpload(prevUploadId);
        } catch (e) {
          console.error('Failed to delete old upload:', e);
        }
      }

      if (resetAfterUpload) {
        // Notify caller first, then snap back to dropzone (no "uploaded" row).
        onChange(useModel ? upload : upload.id);
        setFileName(null);
        setFileSize(null);
      } else {
        setFileName(upload.file_name);
        setFileSize(file.size);
        onChange(useModel ? upload : upload.id);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError(err?.data?.message ?? err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
      onUploadingChange(false);
    }
  };

  // One entry for click + drop; validate in processFile (accept is a string, not dropzone shape).
  const onDrop = useCallback(
    acceptedFiles => {
      const file = acceptedFiles?.[0];
      if (file) processFile(file);
    },
    [maxSize, isFileAllowed, acceptLabel, useModel, value, bucketFolder, modelParams, metadata],
  );

  const actionsDisabled = disabled || loading || deleting || !!verification?.busy;

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    disabled: actionsDisabled,
    noClick: true,
    noKeyboard: true,
    multiple: false,
  });

  const handleDelete = async () => {
    const id = useModel ? value?.id : value;
    if (id) {
      setDeleting(true);
      try {
        await deleteUpload(id);
      } catch (e) {
        console.error('Failed to delete upload:', e);
      }
      setDeleting(false);
    }
    setFileName(null);
    setFileSize(null);
    setUploadError(null);
    onChange(null);
  };

  // ─── Derive the effective visual state ─────────────────────────────────────
  const isUploadFailed = !fileName && !loading && !!uploadError;
  const isUploading = loading || (!!fileName && !!verification?.busy);

  const state = isUploading
    ? DOCUMENT_UPLOAD_SLOT_STATE.UPLOADING
    : isUploadFailed
      ? DOCUMENT_UPLOAD_SLOT_STATE.UPLOAD_FAILED
      : !fileName
        ? DOCUMENT_UPLOAD_SLOT_STATE.IDLE
        : (verification?.state ?? DOCUMENT_UPLOAD_SLOT_STATE.UPLOADED_PENDING);

  const isVerifyFailed =
    state === DOCUMENT_UPLOAD_SLOT_STATE.VERIFY_FAILED_RETRY ||
    state === DOCUMENT_UPLOAD_SLOT_STATE.VERIFY_FAILED_FACE_MISMATCH;
  const isForceAccepted = state === DOCUMENT_UPLOAD_SLOT_STATE.FORCE_ACCEPTED;
  const isVerified = state === DOCUMENT_UPLOAD_SLOT_STATE.VERIFIED;
  const isAccepted = isForceAccepted || isVerified;

  // Dropzone for idle/verify-fail/uploading — not the filename+Replace row.
  const showDropzone = !fileName || isVerifyFailed || loading;

  const errorMessage = isVerifyFailed ? verification?.errorMessage : null;
  const pointsCaption = verification?.pointsCaption;

  const formattedMaxSize = formatMegabytes(maxSize);
  const formattedFileSize = formatMegabytes(fileSize);

  return (
    <div className={cn('flex w-full flex-col gap-2', className)} data-side={side}>
      {showTitle && (
        <div className="flex items-center justify-between">
          <span className="text-xl font-medium text-neutral-7">{heading}</span>
        </div>
      )}
      {description && <p className="m-0 text-xl text-text-secondary font-regular">{description}</p>}
      {additionalInstructions && (
        <FormMessage
          message={additionalInstructions}
          type={MessageType.Info}
          noMargin
          closeable={false}
          noBorder
          rounded="lg"
          fontClass="text-xl font-medium leading-normal"
        />
      )}
      {caption && <span className="text-lg font-regular text-neutral-6">{caption}</span>}
      <input {...getInputProps({ disabled: actionsDisabled, 'aria-label': heading })} />

      {showDropzone ? (
        <div
          {...getRootProps({
            onClick: open,
          })}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center',
            isDragActive
              ? 'border-primary-4 bg-primary-1'
              : loading
                ? 'border-neutral-4 bg-neutral-2'
                : isUploadFailed
                  ? 'border-red-2 bg-red-1'
                  : isVerifyFailed
                    ? 'border-red-2 bg-red-1'
                    : 'border-neutral-4 bg-neutral-2',
          )}>
          <HugeiconsIcon
            icon={
              loading
                ? CloudUploadIcon
                : isUploadFailed
                  ? CloudAlertIcon
                  : isVerifyFailed
                    ? CloudAlertIcon
                    : CloudUploadIcon
            }
            size="28"
            className={
              loading
                ? 'text-primary-4'
                : isUploadFailed
                  ? 'text-red-2'
                  : isVerifyFailed
                    ? 'text-orange-2'
                    : 'text-primary-4'
            }
          />
          <span className="text-lg font-medium text-neutral-7">
            {loading
              ? t('components.documentUploadSlot.uploading')
              : isUploadFailed
                ? t('components.documentUploadSlot.uploadFailedMessage')
                : isVerifyFailed
                  ? t('components.documentUploadSlot.verifyFailedMessage')
                  : heading?.trim()
                    ? t('components.documentUploadSlot.uploadPrompt', { documentName: heading })
                    : t('components.documentUploadSlot.uploadPromptFallback')}
          </span>
          {!loading && isUploadFailed && uploadError && (
            <span className="text-lg font-regular text-red-3">{uploadError}</span>
          )}
          {!loading && isVerifyFailed && errorMessage && (
            <span className="text-lg font-regular text-red-3">{errorMessage}</span>
          )}
          {(acceptLabel || formattedMaxSize) && (
            <span className="text-lg font-light text-neutral-6">
              {[acceptLabel, formattedMaxSize && `max ${formattedMaxSize}`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
          <button
            disabled={actionsDisabled}
            onClick={event => {
              event.stopPropagation();
              open();
            }}
            type="button"
            className="py-2 px-4 h-12 bg-neutral-1 border-neutral-4 text-neutral-7 font-semibold text-lg rounded-lg border shadow-sm">
            {loading
              ? t('components.documentUploadSlot.uploading')
              : isUploadFailed
                ? t('components.documentUploadSlot.retryUpload')
                : isVerifyFailed
                  ? t('components.documentUploadSlot.uploadAgain')
                  : t('components.documentUploadSlot.chooseFile')}
          </button>
        </div>
      ) : (
        <div
          className={cn(
            'flex items-start gap-3 rounded-lg px-4 py-3',
            isAccepted ? 'border-green-2' : 'border-neutral-4',
            rejected ? 'bg-red-1' : 'bg-neutral-2',
          )}>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="break-words text-xl font-medium text-neutral-7">{fileName}</span>
            {!isUploading && !deleting && formattedFileSize && (
              <span className="text-lg font-regular text-neutral-7">{formattedFileSize}</span>
            )}
            {rejected ? (
              <span className="text-lg font-regular text-red-3">{t('statuses.rejected')}</span>
            ) : deleting ? (
              <span className="text-lg font-regular text-orange-3">
                {t('components.documentUploadSlot.deleting')}
              </span>
            ) : (
              isUploading && (
                <span className="text-lg font-regular text-neutral-7">
                  {t('components.documentUploadSlot.uploading')}
                </span>
              )
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {!actionsDisabled && (
              <>
                {!hideDeleteButton && (
                  <Button
                    type="link"
                    small
                    onClick={handleDelete}
                    className="text-red-2 font-regular">
                    {t('common.delete')}
                  </Button>
                )}
                <Button
                  type="link"
                  small
                  onClick={open}
                  className="text-text-tertiary font-regular">
                  {t('components.documentUploadSlot.replace')}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

DocumentUploadSlot.propTypes = {
  side: PropTypes.oneOf(['front', 'back']),
  heading: PropTypes.string,
  caption: PropTypes.string,
  description: PropTypes.string,
  additionalInstructions: PropTypes.string,
  brand: PropTypes.object,
  disabled: PropTypes.bool,
  hideDeleteButton: PropTypes.bool,
  rejected: PropTypes.bool,
  resetAfterUpload: PropTypes.bool,
  value: PropTypes.any,
  useModel: PropTypes.bool,
  modelParams: PropTypes.object,
  metadata: PropTypes.object,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  acceptLabel: PropTypes.string,
  isFileAllowed: PropTypes.func,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  onUploadingChange: PropTypes.func,
  verification: PropTypes.shape({
    state: PropTypes.oneOf(Object.values(DOCUMENT_UPLOAD_SLOT_STATE)),
    errorMessage: PropTypes.node,
    attemptCount: PropTypes.number,
    maxAttempts: PropTypes.number,
    pointsCaption: PropTypes.string,
    busy: PropTypes.bool,
  }),
  className: PropTypes.string,
};
